import React from "react";
import { motion } from "motion/react";
import {
  Eye, Calendar, Star, DollarSign, FileText
} from "lucide-react";
import { Sparkles } from "lucide-react";
import ExpertOnboardingWizard from "../ui/ExpertOnboardingWizard";

export default function ExpertOverviewTab({
  user,
  stats,
  averageRating,
  expertReviews,
  upcomingBookings,
  completedBookings,
  completeBooking,
  setActiveTab,
  setSelectedBooking,
  setSelectedClientForGoals,
  navigate,
  profile,
  setProfile,
  handleSaveProfile,
  onboardingComplete,
  setOnboardingComplete,
  avatarUrl,
  handleAvatarChange
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {user?.role === "expert" && !onboardingComplete && !(user.bio && user.bio.length > 10 && Array.isArray(user.specializations) && user.specializations.length > 0) ? (
        <ExpertOnboardingWizard
          profile={profile}
          setProfile={setProfile}
          userId={user?.id}
          avatarUrl={avatarUrl}
          onAvatarChange={handleAvatarChange}
          onComplete={() => setOnboardingComplete(true)}
          onSaveProfile={async (data) => {
            if (handleSaveProfile) {
              setProfile(data);
              await handleSaveProfile(data);
            }
          }}
        />
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-5 rounded-2xl bg-neutral-900 border border-neutral-800">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-5 h-5 text-blue-400" />
                <span className="text-lg font-extralight text-neutral-400">Просмотры</span>
              </div>
              <p className="text-2xl font-medium text-white">{stats.views.toLocaleString("ru-RU")}</p>
            </div>
            <div className="p-5 rounded-2xl bg-neutral-900 border border-neutral-800">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-5 h-5 text-green-400" />
                <span className="text-lg font-extralight text-neutral-400">Консультаций</span>
              </div>
              <p className="text-2xl font-medium text-white">{stats.consultations}</p>
            </div>
            <div className="p-5 rounded-2xl bg-neutral-900 border border-neutral-800">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-yellow-400" />
                <span className="text-lg font-extralight text-neutral-400">Рейтинг</span>
              </div>
              <button
                onClick={() => { setActiveTab("reviews"); setSelectedBooking(null); setSelectedClientForGoals(null); }}
                className="text-left w-full group/rating"
              >
                <p className="text-2xl font-medium text-white group-hover/rating:text-primary-400 transition-colors">
                  {averageRating > 0 ? averageRating : "—"} <span className="text-lg text-neutral-500">({expertReviews.length})</span>
                </p>
              </button>
            </div>
            <div className="p-5 rounded-2xl bg-neutral-900 border border-neutral-800">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-primary-400" />
                <span className="text-lg font-extralight text-neutral-400">Заработок</span>
              </div>
              <p className="text-2xl font-medium text-white">{stats.earnings.toLocaleString("ru-RU")} ₽</p>
            </div>
          </div>

          {upcomingBookings.length > 0 && (
            <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
              <h3 className="text-xl font-medium text-white mb-4">Ближайшие консультации</h3>
              <div className="space-y-4">
                {upcomingBookings.map((b) => (
                  <div
                    key={b.id}
                    onClick={() => { setActiveTab("consultations"); setSelectedBooking(b); if (typeof window !== "undefined") { window.__expertPrepMode = "prep"; } }}
                    className="p-4 rounded-xl bg-neutral-800/50 cursor-pointer hover:bg-neutral-800/80 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-medium">{b.clientName?.[0] || "K"}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-medium text-white truncate">{b.clientName || "Клиент"}</h4>
                        <p className="text-lg font-extralight text-neutral-400 truncate">{b.topic || "Без темы"}</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-neutral-700/50 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-lg font-extralight">
                        <Calendar className="w-4 h-4 text-primary-400 flex-shrink-0" />
                        <span className="font-medium text-white">
                          {new Date(b.date).toLocaleDateString("ru-RU")}
                        </span>
                        <span className="text-primary-400">{b.time}</span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveTab("consultations"); setSelectedBooking(b); if (typeof window !== "undefined") { window.__expertPrepMode = "prep"; } }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 text-lg font-extralight transition-colors flex-shrink-0"
                      >
                        <Sparkles className="w-4 h-4" />
                        <span className="hidden sm:inline">Подготовиться</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {completedBookings.length > 0 && (
            <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
              <h3 className="text-xl font-medium text-white mb-4">Последние консультации</h3>
              <div className="space-y-4">
                {completedBookings.slice(0, 3).map((b) => (
                  <div
                    key={b.id}
                    onClick={() => { setActiveTab("consultations"); setSelectedBooking(b); }}
                    className="p-4 rounded-xl bg-neutral-800/50 cursor-pointer hover:bg-neutral-800/80 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-medium">{b.clientName?.[0] || "K"}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-medium text-white truncate">{b.clientName || "Клиент"}</h4>
                        <p className="text-lg font-extralight text-neutral-400 truncate">{b.topic || "Без темы"}</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-neutral-700/50 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-lg font-extralight">
                        <Calendar className="w-4 h-4 text-neutral-500 flex-shrink-0" />
                        <span className="text-neutral-500">
                          {new Date(b.date).toLocaleDateString("ru-RU")}
                        </span>
                        <span className="text-green-400">Завершено</span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveTab("consultations"); setSelectedBooking(b); }}
                        className="p-2 rounded-md hover:bg-neutral-700 text-neutral-500 hover:text-primary-400 transition-colors flex-shrink-0"
                        title="Написать заметки"
                      >
                        <FileText className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
}